/**
 * MapController — imperative MapLibre engine behind <MissionMap>.
 *
 * Design:
 *  - Sources are created once per style load; data is pushed with setData (never re-creating
 *    layers per frame). The last data per source is cached so a basemap switch re-installs
 *    overlays instantly.
 *  - Aircraft are animated at rAF between world updates: each new world state becomes an anchor;
 *    the display position dead-reckons from it at the observed world-time rate (≈1 in LIVE, the
 *    playback rate in REPLAY, 0 when paused). A new fix never jumps the symbol: the difference is an
 *    offset that glides to zero on a critically damped spring per axis (velocity kept across fixes),
 *    and the heading turns on an angle spring (VLP-2300).
 *  - 3D view (VLP-2300): pitch + same-origin DEM terrain (terrain3d.ts); aircraft at true height with a
 *    ground shadow, drop line and AGL label, and the forecast ribbon + σ cone drawn at the predicted
 *    altitude by a custom WebGL layer (track3d.ts). 2D stays exactly as before.
 *  - Static-ish sources (places, trails, crew, cameras) update at world rate and only when their
 *    content signature changes.
 *  - Labels are DOM (mono tabular figures), positioned on each render.
 */
import maplibregl, { type GeoJSONSource, type LngLatBoundsLike, type Map as MlMap, type MapMouseEvent } from 'maplibre-gl';
import {
  MAX_EXTRAPOLATE_SEC,
  projectTrack,
  relativeGeometry,
  stepAngleSpring,
  stepSpring,
  type SpringState,
  type TrackSample,
  type Aircraft,
  type AircraftObservation,
  type AircraftState,
  type Airport,
  type EpochMs,
  type Id,
  type LatLon,
  type Marker,
  type MissionBundle,
  type TargetRef,
  type TrafficTarget,
  type WorldState,
} from '@apos/shared';
import * as shared from '@apos/shared';
import type { CameraCoverage } from '@apos/shared'; // coverage lane
import type { BasemapId, SelectionRef } from '../../lib/types';
import { fmt } from '../../lib/fmt';
import { sinceText } from '../../lib/since';
import { offlineStyle, restyleDark, restyleDaylight, styleFor } from './basemaps';
import { mapAttributionControl } from './attribution';
import {
  angleDelta,
  bearing,
  circleRing,
  destination,
  distance,
  KT_TO_MPS,
  metersPerPixel,
  midpoint,
  NM,
  runwayPolygon,
  wedgeRing,
  type LngLat,
} from './geometry';
import { cameraKind, cameraMapLabel, wedgeRangeM } from './cameraStyle';
import { countUpTo, FlightPathCache, splitAtGaps } from './flightPath';
import { aircraftIconId, beamIconId, cameraIconId, flagIconId, installIconFactory, locationIconId } from './icons';
import { esc, LabelLayer, type LabelSpec } from './labels';
import { aircraftColor, aircraftShape, hexToRgb, paletteFor, roleKind, roleStyle, safeHex, type MapPalette } from './palette';
import { trafficLabel, trafficName, trafficOpacity, trafficPosition } from './trafficStyle';
import { TerrainController, type TerrainStatus, type ViewMode } from './terrain3d';
import {
  dropLineMesh,
  emptyMesh,
  ForecastGate,
  forecastMesh,
  forecastOffText,
  glyphMesh,
  rebaseSamples,
  ribbonHorizonSec,
  ringMesh,
  rgbOf,
  quantizeAhead,
  RIBBON_MIN_GS_KT,
  trackFeatures,
  aglMetres,
  heightLabel,
  mslMetres,
  type Mesh,
} from './track3d';
import { Track3dLayer } from './track3dLayer';
import {
  CAMERA_TRANSITION_MS,
  CHASE_PITCH,
  easeSpring,
  FOCUS_GHOST_MS,
  FOLLOW_TAU,
  GLIDE_RESPONSE_SEC,
  MAX_PITCH_3D,
  PITCH_3D,
  prefersReducedMotion,
} from './cameraMotion';

type FC = GeoJSON.FeatureCollection;
const EMPTY: FC = { type: 'FeatureCollection', features: [] };

const SOURCES = [
  'apos-places',
  'apos-fov',
  'apos-crew-acc',
  'apos-paths',
  'apos-trails',
  'apos-traffic',
  'apos-sel',
  'apos-pairs',
  'apos-sun',
  'apos-markers',
  'apos-cams',
  'apos-beam',
  'apos-crew',
  'apos-live',
  'apos-ghost',
] as const;
type SourceId = (typeof SOURCES)[number];

const CLICK_LAYERS = ['apos-ac', 'apos-acam', 'apos-crew', 'apos-cams', 'apos-markers', 'apos-loc', 'apos-apt', 'apos-traffic'];
const CLICK_PRIORITY: Record<string, number> = { aircraft: 0, crew: 1, camera: 2, marker: 3, location: 4, airport: 5, traffic: 6 };

export interface ControllerProps {
  bundle: MissionBundle;
  world: WorldState | null;
  selection: SelectionRef | null;
  pairs: Array<{ from: TargetRef; to: TargetRef }>;
  showSunRay: boolean;
  /** Anchor for the sun ray when nothing is selected (default: mission centre). */
  sunRayFrom: TargetRef | null;
  followAircraftId: string | null;
  basemap: BasemapId;
  markers: Marker[];
  showRangeRings: boolean;
  showLeaders: boolean;
  trackUp: boolean;
  padding: { top: number; right: number; bottom: number; left: number };
  /** Other (non-mission) traffic; null = no traffic layer. */
  traffic: TrafficTarget[] | null;
  showTraffic: boolean;
  /** Whole-mission flight paths from these observations (cut at world.t), since `pathSince`. */
  showFlightPath: boolean;
  pathObs: Record<Id, AircraftObservation[]> | null;
  /** This phone's compass heading (°T): drawn as a facing cone on my own crew dot. */
  myHeadingDeg?: number | null;
  pathSince: EpochMs | null;
  /**
   * Coverage lane (round 3): per-camera coverage of the tracked aircraft. In-frame cameras draw an
   * "active" (green, solid) cone; Manned cameras that can cover it aim their cone at the aircraft.
   */
  cameraCoverage?: ReadonlyArray<CameraCoverage> | null;
  /** Aircraft to point at with an edge arrow when it is off-screen (LIVE "find"); null = none. */
  findAircraftId?: string | null;
  /** Plain flight status per aircraft ("Taking off"), shown in its map label (LIVE). */
  aircraftStatus?: Record<string, string> | null;
  /** Viewing mode (round 4): layer groups hidden by visibility only — the data keeps updating. */
  hiddenGroups?: ReadonlyArray<HiddenGroup>;
  /** Frame the bundle's airports when fitting (default true). Fleets turns it off: its airports follow the view, not the subject. */
  fitAirports?: boolean;
  /** An aircraft whose position is long out of date shows its name and "last seen", not altitude and speed as if they were current (Fleets). */
  quietLost?: boolean;
  /** Extra points every fit must include (a recorded flight's track, so replay opens on all of it). */
  frame?: ReadonlyArray<LatLon>;
  /** 2D (default, unchanged) or 3D: pitch, terrain, aircraft at true height (VLP-2300). */
  viewMode?: ViewMode;
}

/** Layers of the 2D forecast (ribbon, cone, ticks): hidden in 3D, where the GL layer draws them at height. */
const FORECAST_2D_LAYERS = ['apos-cone', 'apos-cone-line', 'apos-leaders', 'apos-leaders-projected', 'apos-ticks'];
/** 2D-only aircraft adornments that move to height in 3D. */
const RING_2D_LAYERS = ['apos-ac-ring'];

/** Layer groups a viewing mode can hide by visibility. */
export type HiddenGroup = 'cones' | 'cameras' | 'markers';

/**
 * Map layers + DOM label groups per hideable group. Cones match by prefix so any further FOV
 * layers ("apos-fov-…") follow the same switch.
 */
const GROUP_LAYERS: Record<HiddenGroup, { layer: (id: string) => boolean; labels: string[] }> = {
  cones: { layer: (id) => id.startsWith('apos-fov'), labels: [] },
  cameras: { layer: (id) => id === 'apos-cams' || id === 'apos-acam', labels: ['cams'] },
  markers: { layer: (id) => id === 'apos-markers', labels: ['markers'] },
};

/** Where the off-screen arrow goes: anchor (container px) on the padded edge, pointing `angleDeg` (0 = up, clockwise). */
export interface EdgeInfo {
  x: number;
  y: number;
  angleDeg: number;
  /** Which edge: the pill hugs it (left/right: x is that edge; top/bottom: x is the pill centre). */
  side: 'left' | 'right' | 'top' | 'bottom';
}

export interface ControllerCallbacks {
  onSelect(sel: SelectionRef | null): void;
  onMapClick?: ((p: LatLon) => void) | undefined;
  onFollowBreak?(): void;
  onViewChange?(v: { bearing: number; zoom: number; offline: boolean; loaded: boolean }): void;
  /** The find aircraft is off-screen (edge anchor) or on-screen / unknown (null). Throttled, on change only. */
  onEdge?(e: EdgeInfo | null): void;
  /** 3D terrain state (off in 2D). */
  onTerrain?(s: TerrainStatus): void;
  /** "Forecast off: position 42 s old" for the focus aircraft, or null. On change only. */
  onForecastOff?(text: string | null): void;
}

interface Anchor {
  state: AircraftState;
  perf: number;
  /** Display − dead-reckoned position (deg), gliding to 0 on a spring; velocity in deg/s. */
  offLat: SpringState;
  offLon: SpringState;
  /** Displayed heading (°), an angle spring toward the track. */
  rot: SpringState;
  disp: { lat: number; lon: number };
  /** Dead-reckoning velocity (deg/s) last frame: carried into the offset when a new fix arrives. */
  drVel: { lat: number; lon: number };
  /** Seconds the display is dead-reckoned ahead of the state (for re-anchoring the forecast). */
  ahead: number;
  /** Forecast from this state (class E), computed once per world update. */
  samples: TrackSample[];
  /** 3D render height (m) of the aircraft glyph; null in 2D / altitude unknown. */
  elev: number | null;
}

const now = () => performance.now();
/** Coverage lane: cone colour of a camera that has the aircraft in frame. */
const COVERAGE_ACTIVE_DARK = '#4ade80';
const COVERAGE_ACTIVE_LIGHT = '#15803d';
const MAX_AHEAD_S = 2.5;
/** Spring offsets below this (deg ≈ 1 cm) / headings within this (°) count as settled. */
const SETTLED_DEG = 1e-7;
const SETTLED_ROT = 0.05;
/** Bigger corrections than this (deg, ≈ 600 m) jump instead of gliding. */
const GLIDE_MAX_DLAT = 0.0055;
const GLIDE_MAX_DLON = 0.007;
/** Dead-reckoning shorter than this is normal inter-sample gap, not worth flagging visually. */
const EST_VISUAL_SEC = 3;

export class MapController {
  readonly map: MlMap;
  private labels: LabelLayer;
  private props: ControllerProps;
  private cb: ControllerCallbacks;
  private palette: MapPalette;
  private cache = new Map<SourceId, FC>();
  private installed = false;
  private styleLoaded = false;
  private offline = false;
  private styleTimer: ReturnType<typeof setTimeout> | null = null;
  private currentBasemap: BasemapId;
  private raf = 0;
  private destroyed = false;
  private anchors = new Map<string, Anchor>();
  private worldT: number | null = null;
  private worldPerf = 0;
  private timeRate = 0;
  private trailLayerIds = new Set<string>();
  private trailSig = '';
  private liveDirty = true;
  private overlayDirty = true;
  private lastOverlayPerf = 0;
  private fittedOnce = false;
  private removeIconFactory: () => void;
  private followBearing = 0;
  private lastFrame = now();
  private hoverRaf = 0;
  private aircraftById = new Map<string, Aircraft>();
  private paths = new FlightPathCache();
  private pathSig = '';
  private edgeSig = '';
  private lastEdgePerf = 0;
  private viewMode: ViewMode = '2d';
  private terrain: TerrainController;
  private terrainStatus: TerrainStatus = 'off';
  private layer3d = new Track3dLayer('apos-3d');
  private reducedMotion = prefersReducedMotion();
  private motionQuery: MediaQueryList | null = null;
  private chasePitch = CHASE_PITCH;
  private ghost: { lat: number; lon: number; altM: number | null; until: number } | null = null;
  private forecastOff: string | null = null;

  constructor(container: HTMLElement, props: ControllerProps, cb: ControllerCallbacks) {
    this.props = props;
    this.cb = cb;
    this.palette = paletteFor(props.basemap);
    this.currentBasemap = props.basemap;
    const startOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    this.offline = startOffline;
    this.map = new maplibregl.Map({
      container,
      style: startOffline ? offlineStyle(this.palette.light) : styleFor(props.basemap),
      center: [props.bundle.mission.center.lon, props.bundle.mission.center.lat],
      zoom: 11.5,
      attributionControl: false,
      // 2D: pitch is clamped to 0, so right-drag only rotates (as before); 3D raises maxPitch
      maxPitch: 0,
      dragRotate: true,
      pitchWithRotate: true,
      touchPitch: false,
      fadeDuration: 0,
      renderWorldCopies: false,
      cancelPendingTileRequestsWhileZooming: true,
      validateStyle: false,
    });
    this.map.touchZoomRotate.disableRotation();
    // the view's padding (HUD, side panel, sheet): centring, follow and find all aim at the clear area
    this.applyPadding(props.padding);
    this.map.addControl(mapAttributionControl(), 'bottom-right');
    this.map.addControl(new maplibregl.ScaleControl({ unit: 'nautical', maxWidth: 110 }), 'bottom-left');
    // dev/e2e only: lets Playwright find and tap map symbols (never in production builds)
    if (import.meta.env.DEV) (container as unknown as { __aposMap?: MlMap }).__aposMap = this.map;
    if (import.meta.env.DEV) (container as unknown as { __aposCtrl?: MapController }).__aposCtrl = this;
    this.labels = new LabelLayer(this.map);
    this.removeIconFactory = installIconFactory(this.map);
    this.terrain = new TerrainController(this.map, (st) => {
      this.terrainStatus = st;
      this.liveDirty = true;
      this.cb.onTerrain?.(st);
    });
    this.indexAircraft();

    this.map.on('style.load', this.onStyleLoad);
    this.map.on('error', this.onError);
    this.map.on('click', this.onClick);
    this.map.on('mousemove', this.onHover);
    this.map.on('move', this.onMove);
    this.map.on('rotate', this.emitView);
    this.map.on('zoomend', this.emitView);
    this.map.on('dragstart', this.onDragStart);
    this.map.on('pitchend', this.onPitchEnd);
    this.map.on('render', this.onRender);
    this.armStyleTimeout();
    window.addEventListener('online', this.onOnline);
    this.motionQuery = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.motionQuery?.addEventListener?.('change', this.onMotionPref);
    this.raf = requestAnimationFrame(this.frame);
  }

  // ───────────── public API ─────────────

  setProps(next: ControllerProps, cb: ControllerCallbacks): void {
    const prev = this.props;
    this.props = next;
    this.cb = cb;
    if (next.basemap !== this.currentBasemap) this.setBasemap(next.basemap);
    if (next.padding !== prev.padding) this.applyPadding(next.padding);
    if (next.bundle !== prev.bundle) {
      this.indexAircraft();
      this.pushPlaces();
      this.ensureTrailLayers();
    }
    if (next.world !== prev.world) this.onWorld(prev.world, next.world);
    if (next.markers !== prev.markers || next.bundle !== prev.bundle) this.pushMarkers();
    if (next.selection !== prev.selection || next.pairs !== prev.pairs || next.showSunRay !== prev.showSunRay || next.sunRayFrom !== prev.sunRayFrom || next.showRangeRings !== prev.showRangeRings) {
      this.overlayDirty = true;
      this.liveDirty = true;
      this.pushWorldStatic();
    }
    if (next.showLeaders !== prev.showLeaders) this.liveDirty = true;
    if (next.cameraCoverage !== prev.cameraCoverage && next.world === prev.world) this.pushWorldStatic(); // coverage lane
    else if (next.aircraftStatus !== prev.aircraftStatus) this.pushWorldStatic();
    if (next.myHeadingDeg !== prev.myHeadingDeg) this.pushBeam();
    if (next.hiddenGroups !== prev.hiddenGroups) this.applyHiddenGroups();
    if (next.traffic !== prev.traffic || next.showTraffic !== prev.showTraffic || (next.selection !== prev.selection && (next.selection?.kind === 'traffic' || prev.selection?.kind === 'traffic'))) this.pushTraffic();
    if (next.pathObs !== prev.pathObs || next.showFlightPath !== prev.showFlightPath || next.pathSince !== prev.pathSince || next.bundle !== prev.bundle) this.pushPaths();
    if (next.followAircraftId !== prev.followAircraftId && !next.followAircraftId) {
      // leaving follow: return to north-up if we rotated
      if (Math.abs(this.map.getBearing()) > 0.5 && next.trackUp) this.map.easeTo({ bearing: 0, duration: 400 });
    }
    if (next.trackUp !== prev.trackUp && !next.trackUp && Math.abs(this.map.getBearing()) > 0.5) this.map.easeTo({ bearing: 0, duration: 400 });
    if ((next.viewMode ?? '2d') !== this.viewMode) this.setViewMode(next.viewMode ?? '2d');
    else if (next.followAircraftId !== prev.followAircraftId && next.followAircraftId && this.viewMode === '3d') this.chasePitch = CHASE_PITCH;
  }

  get mode(): ViewMode {
    return this.viewMode;
  }

  /**
   * 2D ↔ 3D. 3D raises maxPitch to 70°, enables touch pitch, drapes the DEM (or says it is unavailable)
   * and lifts aircraft to their true height. The camera moves from where it is now in ≤ 200 ms
   * (--ease-spring); reduced motion jumps.
   */
  setViewMode(mode: ViewMode): void {
    if (mode === this.viewMode) return;
    this.viewMode = mode;
    const m = this.map;
    if (mode === '3d') {
      m.setMaxPitch(MAX_PITCH_3D);
      m.touchPitch.enable();
      this.chasePitch = CHASE_PITCH;
      if (this.styleLoaded) this.terrain.set(true, this.offline);
      this.moveCamera({ pitch: this.props.followAircraftId ? CHASE_PITCH : Math.max(m.getPitch(), PITCH_3D) });
      this.labels.setProjector((lon, lat, elevM) => this.layer3d.project(lon, lat, elevM));
    } else {
      this.terrain.set(false, this.offline);
      m.touchPitch.disable();
      this.labels.setProjector(null);
      // maxPitch returns to 0 once the camera is level (reconcileFlat)
      this.moveCamera({ pitch: 0 });
    }
    this.applyViewLayers();
    this.pushGhost();
    this.pushWorldStatic();
    this.liveDirty = true;
    this.overlayDirty = true;
  }

  /**
   * Ease to a point (a predicted moment) and leave a small hollow ghost there for ~3 s. In 3D the pitch
   * is kept. A follow is released so the camera stays where it was asked to go.
   */
  focusOn(lat: number, lon: number, altM: number | null): void {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    if (this.props.followAircraftId) this.cb.onFollowBreak?.();
    this.moveCamera({ center: [lon, lat] });
    this.ghost = { lat, lon, altM: altM != null && Number.isFinite(altM) ? altM : null, until: now() + FOCUS_GHOST_MS };
    this.pushGhost();
    this.liveDirty = true;
  }

  /** easeTo from the current camera (≤ 200 ms, --ease-spring), or jumpTo with reduced motion. */
  private moveCamera(opts: { center?: [number, number]; pitch?: number; bearing?: number }): void {
    if (this.reducedMotion) this.map.jumpTo(opts);
    else this.map.easeTo({ ...opts, duration: CAMERA_TRANSITION_MS, easing: easeSpring });
  }

  setBasemap(b: BasemapId): void {
    this.currentBasemap = b;
    this.palette = paletteFor(b);
    this.styleLoaded = false;
    this.installed = false;
    this.trailLayerIds.clear();
    this.offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    this.map.setStyle(this.offline ? offlineStyle(this.palette.light) : styleFor(b), { diff: false });
    this.armStyleTimeout();
    // colours depend on the palette: rebuild cached data
    this.pushPlaces();
    this.pushMarkers();
    this.pushWorldStatic();
    this.pathSig = '';
    this.pushPaths();
    this.pushTraffic();
    this.liveDirty = true;
  }

  zoomBy(delta: number): void {
    this.map.easeTo({ zoom: this.map.getZoom() + delta, duration: 250 });
  }

  resetNorth(): void {
    this.map.easeTo({ bearing: 0, duration: 400 });
  }

  /** Manual fit includes remote crew; initial framing prioritizes the shoot and aircraft. */
  fitAll(animate = true, includeCrew = true): void {
    const pts: LatLon[] = [this.props.bundle.mission.center];
    for (const l of this.props.bundle.locations) pts.push(l);
    const w = this.props.world;
    if (w) {
      for (const a of w.aircraft) if (a.freshness !== 'lost') pts.push(a);
      for (const c of w.crew) if (includeCrew && c.freshness !== 'lost') pts.push(c);
      for (const c of w.cameras) if (c.position) pts.push(c.position);
    }
    if (this.props.frame) for (const p of this.props.frame) pts.push(p);
    const primary = this.props.bundle.missionAirports.find((m) => m.role === 'primary')?.icao;
    if (this.props.fitAirports !== false) for (const a of this.props.bundle.airports) if (!primary || a.icao === primary) pts.push(a);
    this.fitPoints(pts, animate);
  }

  /**
   * Find: frame an aircraft (its displayed, dead-reckoned position) together with `with` (me / the
   * reference). With nothing to pair, centre on the aircraft at the current zoom.
   */
  frameAircraft(aircraftId: string, withPos: LatLon | null, animate = true): void {
    const a = this.anchors.get(aircraftId);
    const s = a ? null : this.props.world?.aircraft.find((x) => x.aircraftId === aircraftId);
    const pos = a ? a.disp : s ? { lat: s.lat, lon: s.lon } : null;
    if (!pos) return;
    if (!withPos) {
      this.map.easeTo({ center: [pos.lon, pos.lat], duration: animate ? 600 : 0 });
      return;
    }
    this.fitPoints([pos, withPos], animate, 15);
  }

  /** Fit points inside the padded view (single point → centre at zoom 12). */
  private fitPoints(pts: LatLon[], animate: boolean, maxZoom = 14): void {
    if (pts.length === 1) {
      this.map.easeTo({ center: [pts[0]!.lon, pts[0]!.lat], zoom: 12, duration: animate ? 600 : 0 });
      return;
    }
    let minLat = 90;
    let maxLat = -90;
    let minLon = 180;
    let maxLon = -180;
    for (const p of pts) {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
      minLat = Math.min(minLat, p.lat);
      maxLat = Math.max(maxLat, p.lat);
      minLon = Math.min(minLon, p.lon);
      maxLon = Math.max(maxLon, p.lon);
    }
    const bounds: LngLatBoundsLike = [
      [minLon, minLat],
      [maxLon, maxLat],
    ];
    try {
      // the view padding is on the map transform (applyPadding): this is only the margin inside it
      this.map.fitBounds(bounds, {
        padding: { top: 60, right: 70, bottom: 60, left: 60 },
        maxZoom,
        duration: animate ? 700 : 0,
        bearing: this.map.getBearing(),
      });
    } catch {
      /* container too small */
    }
  }

  /** Overlays the view must not frame things under (HUD strip, side panel, bottom sheet). */
  private applyPadding(pad: ControllerProps['padding']): void {
    const cur = this.map.getPadding();
    if (cur.top === pad.top && cur.right === pad.right && cur.bottom === pad.bottom && cur.left === pad.left) return;
    const c = this.map.getContainer();
    // a padding larger than the map (tiny embeds) would invert the view: keep at least 120 px clear
    const w = c.clientWidth || 0;
    const h = c.clientHeight || 0;
    const fit = (a: number, b: number, size: number) => (size && a + b > size - 120 ? Math.max(0, Math.floor(((size - 120) * a) / Math.max(1, a + b))) : a);
    this.map.setPadding({ top: fit(pad.top, pad.bottom, h), bottom: fit(pad.bottom, pad.top, h), left: fit(pad.left, pad.right, w), right: fit(pad.right, pad.left, w) });
  }

  resize(): void {
    this.map.resize();
    this.labels.markDirty();
  }

  get isOffline(): boolean {
    return this.offline;
  }

  destroy(): void {
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    cancelAnimationFrame(this.hoverRaf);
    if (this.styleTimer) clearTimeout(this.styleTimer);
    window.removeEventListener('online', this.onOnline);
    this.motionQuery?.removeEventListener?.('change', this.onMotionPref);
    this.terrain.destroy();
    this.removeIconFactory();
    this.labels.destroy();
    this.map.remove();
  }

  // ───────────── style lifecycle ─────────────

  private armStyleTimeout(): void {
    if (this.styleTimer) clearTimeout(this.styleTimer);
    if (this.offline) return;
    this.styleTimer = setTimeout(() => {
      if (!this.styleLoaded && !this.destroyed) this.fallbackOffline();
    }, 9000);
  }

  private fallbackOffline(): void {
    if (this.offline) return;
    this.offline = true;
    this.installed = false;
    this.trailLayerIds.clear();
    this.map.setStyle(offlineStyle(this.palette.light), { diff: false });
    this.emitView();
  }

  private onOnline = () => {
    if (this.offline && !this.destroyed) this.setBasemap(this.currentBasemap);
  };

  private onError = (e: { error?: Error & { status?: number; url?: string }; sourceId?: string }) => {
    // A failed style/sprite/glyph fetch before the style ever loads → offline basemap.
    if (this.styleLoaded || this.offline) return;
    const msg = `${e.error?.message ?? ''} ${e.error?.url ?? ''}`;
    if (!e.sourceId && /style|fetch|Failed|NetworkError|openfreemap|403|404|5\d\d/i.test(msg)) this.fallbackOffline();
  };

  private onStyleLoad = () => {
    this.styleLoaded = true;
    if (this.styleTimer) clearTimeout(this.styleTimer);
    if (!this.offline) {
      if (this.currentBasemap === 'dark') restyleDark(this.map);
      else if (this.currentBasemap === 'daylight') restyleDaylight(this.map);
    }
    this.installOverlays();
    this.terrain.reinstall(this.offline);
    if (this.viewMode === '3d') this.terrain.set(true, this.offline);
    this.emitView();
    // A world present from construction (e.g. Replay opened paused at a deep-linked t) never
    // reaches setProps as a change: anchor + fit it here once.
    const w = this.props.world;
    if (w && this.worldT === null) this.onWorld(null, w);
  };

  private emitView = () => {
    this.labels.markDirty();
    this.cb.onViewChange?.({ bearing: this.map.getBearing(), zoom: this.map.getZoom(), offline: this.offline, loaded: this.styleLoaded });
  };

  // ───────────── overlay install ─────────────

  private installOverlays(): void {
    const m = this.map;
    const p = this.palette;
    for (const id of SOURCES) {
      if (!m.getSource(id)) m.addSource(id, { type: 'geojson', data: this.cache.get(id) ?? EMPTY, lineMetrics: id === 'apos-trails' });
    }
    const k = (v: string): maplibregl.ExpressionSpecification => ['==', ['get', 'k'], v];
    const heavy = p.light ? 1.35 : 1;

    // places
    m.addLayer({ id: 'apos-loc-r-fill', type: 'fill', source: 'apos-places', filter: k('loc-r'), paint: { 'fill-color': ['get', 'c'], 'fill-opacity': 0.06 } });
    m.addLayer({
      id: 'apos-loc-r-line',
      type: 'line',
      source: 'apos-places',
      filter: k('loc-r'),
      paint: { 'line-color': ['get', 'c'], 'line-opacity': 0.5, 'line-width': 1 * heavy, 'line-dasharray': [3, 3] },
    });
    m.addLayer({ id: 'apos-rwy', type: 'fill', source: 'apos-places', filter: k('rwy'), paint: { 'fill-color': p.runway, 'fill-outline-color': p.runwayLine } });
    m.addLayer({
      id: 'apos-rwy-line',
      type: 'line',
      source: 'apos-places',
      filter: k('rwy'),
      paint: { 'line-color': p.runwayLine, 'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 14, 1.2 * heavy] },
    });
    m.addLayer({
      id: 'apos-rwy-cl',
      type: 'line',
      source: 'apos-places',
      filter: k('rwy-cl'),
      minzoom: 12.5,
      paint: { 'line-color': p.runwayLine, 'line-width': 1, 'line-dasharray': [4, 4] },
    });

    // camera FOV wedges
    // Static cameras and Manned cameras aimed by their operator's phone: clear wedge along the aim.
    // Manned with the aim unknown (phone facing stale / inaccurate): fainter, dashed, pointing at the
    // aircraft ("can cover"). A zoom draws its WIDE end faint with the LONG end inside it, stronger.
    const wide: maplibregl.ExpressionSpecification = ['==', ['get', 'tier'], 'wide'];
    m.addLayer({
      id: 'apos-fov-fill',
      type: 'fill',
      source: 'apos-fov',
      paint: {
        // coverage lane: a camera with the aircraft in frame reads "active" (green)
        'fill-color': ['case', ['==', ['get', 'act'], true], p.light ? COVERAGE_ACTIVE_LIGHT : COVERAGE_ACTIVE_DARK, p.camera],
        'fill-opacity': [
          'case',
          wide, p.light ? 0.05 : 0.06,
          ['==', ['get', 'act'], true], p.light ? 0.2 : 0.2,
          ['get', 'faint'], p.light ? 0.06 : 0.07,
          ['get', 'rec'], p.light ? 0.2 : 0.22,
          p.light ? 0.12 : 0.14,
        ],
      },
    });
    m.addLayer({
      id: 'apos-fov-line',
      type: 'line',
      source: 'apos-fov',
      paint: {
        'line-color': ['case', ['==', ['get', 'act'], true], p.light ? COVERAGE_ACTIVE_LIGHT : COVERAGE_ACTIVE_DARK, p.fovLine],
        'line-width': ['case', wide, 1 * heavy, ['==', ['get', 'act'], true], 2 * heavy, 1 * heavy],
        'line-opacity': ['case', wide, ['case', ['get', 'sel'], 0.7, 0.4], ['get', 'sel'], 1, ['==', ['get', 'act'], true], 0.95, ['get', 'faint'], 0.45, 0.75],
        'line-dasharray': ['case', ['get', 'faint'], ['literal', [3, 3]], wide, ['literal', [1, 2]], ['literal', [1, 0]]],
      },
    });

    // crew accuracy
    m.addLayer({ id: 'apos-acc-fill', type: 'fill', source: 'apos-crew-acc', paint: { 'fill-color': ['get', 'c'], 'fill-opacity': 0.1 } });
    m.addLayer({ id: 'apos-acc-line', type: 'line', source: 'apos-crew-acc', paint: { 'line-color': ['get', 'c'], 'line-opacity': 0.45, 'line-width': 1 } });

    // whole-mission flight paths: thin, role-coloured, under the fading trails
    m.addLayer({
      id: 'apos-path',
      type: 'line',
      source: 'apos-paths',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': ['get', 'c'], 'line-width': ['get', 'w'], 'line-opacity': ['get', 'op'] },
    });

    // trails (one layer per aircraft — line-gradient cannot be data-driven)
    this.ensureTrailLayers();

    // selection range rings
    m.addLayer({
      id: 'apos-rings',
      type: 'line',
      source: 'apos-sel',
      filter: k('ring'),
      paint: { 'line-color': p.ring, 'line-width': 1 * heavy, 'line-dasharray': [2, 3] },
    });

    // sun
    m.addLayer({
      id: 'apos-sun-ray',
      type: 'line',
      source: 'apos-sun',
      filter: k('ray'),
      layout: { 'line-cap': 'round' },
      paint: {
        'line-color': p.sun,
        'line-width': 2.2 * heavy,
        'line-opacity': ['case', ['get', 'below'], 0.45, 0.95],
        'line-dasharray': ['case', ['get', 'below'], ['literal', [1, 2]], ['literal', [1, 0]]],
      },
    });
    m.addLayer({
      id: 'apos-sun-shadow',
      type: 'line',
      source: 'apos-sun',
      filter: k('shadow'),
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': p.sun, 'line-width': 1.5 * heavy, 'line-opacity': 0.7 },
    });
    m.addLayer({
      id: 'apos-sun-dot',
      type: 'circle',
      source: 'apos-sun',
      filter: k('end'),
      paint: { 'circle-radius': 4, 'circle-color': p.sun, 'circle-stroke-color': p.light ? '#fff' : '#000', 'circle-stroke-width': 1.5 },
    });

    // geometry pairs
    m.addLayer({
      id: 'apos-pairs-line',
      type: 'line',
      source: 'apos-pairs',
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': p.ink, 'line-width': 1.4 * heavy, 'line-opacity': 0.85, 'line-dasharray': [4, 3] },
    });

    // live aircraft: forecast (σ cone + ribbon + time ticks), heads, freshness rings, halo, icons.
    // The forecast is class E (aviation spec §2/§11): the cone is always drawn with the ribbon; solid
    // tier = solid line, projected tier = dashed.
    m.addLayer({ id: 'apos-cone', type: 'fill', source: 'apos-live', filter: k('cone'), paint: { 'fill-color': ['get', 'c'], 'fill-opacity': p.light ? 0.1 : 0.08 } });
    m.addLayer({
      id: 'apos-cone-line',
      type: 'line',
      source: 'apos-live',
      filter: k('cone'),
      paint: { 'line-color': ['get', 'c'], 'line-width': 1, 'line-opacity': p.light ? 0.35 : 0.28 },
    });
    m.addLayer({
      id: 'apos-leaders',
      type: 'line',
      source: 'apos-live',
      filter: ['all', k('ribbon'), ['==', ['get', 'tier'], 'solid']],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': ['get', 'c'], 'line-width': 2.2 * heavy, 'line-opacity': 0.85 },
    });
    m.addLayer({
      id: 'apos-leaders-projected',
      type: 'line',
      source: 'apos-live',
      filter: ['all', k('ribbon'), ['==', ['get', 'tier'], 'projected']],
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: { 'line-color': ['get', 'c'], 'line-width': 1.6 * heavy, 'line-opacity': 0.7, 'line-dasharray': [2, 2.5] },
    });
    m.addLayer({
      id: 'apos-ticks',
      type: 'circle',
      source: 'apos-live',
      filter: k('tick'),
      paint: {
        'circle-radius': ['case', ['==', ['get', 's'], 60], 3.4, 2.6],
        'circle-color': ['get', 'c'],
        'circle-opacity': 0.85,
        'circle-stroke-width': 1,
        'circle-stroke-color': p.light ? '#ffffff' : '#0a0b0d',
      },
    });
    // dead-reckoned position: "probably within" ring, growing with time since the last fix
    m.addLayer({ id: 'apos-est-fill', type: 'fill', source: 'apos-live', filter: k('est'), paint: { 'fill-color': ['get', 'c'], 'fill-opacity': 0.06 } });
    m.addLayer({
      id: 'apos-est-line',
      type: 'line',
      source: 'apos-live',
      filter: k('est'),
      paint: { 'line-color': ['get', 'c'], 'line-width': 1, 'line-opacity': 0.55, 'line-dasharray': [2, 2] },
    });
    m.addLayer({
      id: 'apos-heads',
      type: 'line',
      source: 'apos-live',
      filter: ['all', k('head'), ['!', ['get', 'est']]],
      paint: { 'line-color': ['get', 'c'], 'line-width': 2 * heavy, 'line-opacity': 0.9 },
    });
    m.addLayer({
      id: 'apos-heads-est',
      type: 'line',
      source: 'apos-live',
      filter: ['all', k('head'), ['get', 'est']],
      paint: { 'line-color': ['get', 'c'], 'line-width': 2 * heavy, 'line-opacity': 0.9, 'line-dasharray': [1.5, 1.5] },
    });

    // other traffic: small faint arrows under everything that belongs to the mission
    m.addLayer({
      id: 'apos-traffic',
      type: 'symbol',
      source: 'apos-traffic',
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-rotate': ['get', 'rot'],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': ['case', ['get', 'sel'], 0.62, 0.5],
      },
      paint: { 'icon-opacity': ['case', ['get', 'sel'], 1, ['get', 'op']] },
    });

    // places symbols
    m.addLayer({
      id: 'apos-apt',
      type: 'circle',
      source: 'apos-places',
      filter: k('apt'),
      maxzoom: 12.5,
      paint: { 'circle-radius': 5, 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': p.runwayLine, 'circle-stroke-width': 1.5 },
    });
    m.addLayer({
      id: 'apos-loc',
      type: 'symbol',
      source: 'apos-places',
      filter: k('loc'),
      layout: { 'icon-image': ['get', 'icon'], 'icon-allow-overlap': true, 'icon-ignore-placement': true },
    });

    // markers
    m.addLayer({
      id: 'apos-markers',
      type: 'symbol',
      source: 'apos-markers',
      layout: { 'icon-image': ['get', 'icon'], 'icon-anchor': 'bottom-left', 'icon-offset': [-7, 2], 'icon-allow-overlap': true, 'icon-ignore-placement': true },
    });

    // cameras
    m.addLayer({
      id: 'apos-cams',
      type: 'symbol',
      source: 'apos-cams',
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-rotate': ['get', 'rot'],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-offset': ['get', 'off'],
      },
      paint: { 'icon-opacity': ['get', 'op'] },
    });

    // my facing cone (under my dot)
    m.addLayer({
      id: 'apos-beam',
      type: 'symbol',
      source: 'apos-beam',
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-rotate': ['get', 'rot'],
        'icon-rotation-alignment': 'map',
        'icon-pitch-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });

    // crew
    m.addLayer({
      id: 'apos-crew',
      type: 'circle',
      source: 'apos-crew',
      paint: {
        'circle-radius': ['case', ['get', 'sel'], 8, 6.5],
        'circle-color': ['get', 'c'],
        'circle-opacity': ['get', 'op'],
        'circle-stroke-color': p.light ? '#ffffff' : '#0a0b0d',
        'circle-stroke-width': 2,
        'circle-stroke-opacity': ['get', 'op'],
      },
    });

    // 3D: the aircraft's ground shadow (its footprint on the terrain under the elevated glyph)
    m.addLayer({
      id: 'apos-ac-shadow',
      type: 'circle',
      source: 'apos-live',
      filter: k('shadow'),
      paint: {
        'circle-radius': ['get', 'r'],
        'circle-color': p.shadow,
        'circle-opacity': p.light ? 0.28 : 0.55,
        'circle-blur': 0.45,
        'circle-pitch-alignment': 'map',
        'circle-stroke-color': p.ink,
        'circle-stroke-width': 1,
        'circle-stroke-opacity': p.light ? 0.3 : 0.35,
      },
    });

    // selection halo (under aircraft)
    m.addLayer({
      id: 'apos-halo',
      type: 'circle',
      source: 'apos-sel',
      filter: k('halo'),
      paint: {
        'circle-radius': ['get', 'r'],
        'circle-color': p.halo,
        'circle-opacity': 0.08,
        'circle-stroke-color': p.halo,
        'circle-stroke-width': 1.5,
        'circle-stroke-opacity': 0.9,
      },
    });
    m.addLayer({
      id: 'apos-ac-ring',
      type: 'circle',
      source: 'apos-live',
      filter: ['all', k('ac'), ['has', 'ring']],
      paint: {
        'circle-radius': ['get', 'rr'],
        'circle-color': 'rgba(0,0,0,0)',
        'circle-stroke-color': ['get', 'ring'],
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 0.9,
      },
    });
    m.addLayer({
      id: 'apos-ac',
      type: 'symbol',
      source: 'apos-live',
      filter: k('ac'),
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-rotate': ['get', 'rot'],
        'icon-rotation-alignment': 'map',
        // flat on the ground (only differs once pitched: in 3D this is the footprint under the glyph)
        'icon-pitch-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': ['*', ['get', 'sz'], ['case', ['get', 'sel'], 1.12, 1]],
        'symbol-sort-key': ['get', 'z'],
      },
      paint: { 'icon-opacity': ['get', 'op'] },
    });
    // Aerial camera badge riding beside its aircraft
    m.addLayer({
      id: 'apos-acam',
      type: 'symbol',
      source: 'apos-live',
      filter: k('acam'),
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-offset': ['get', 'off'],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });

    // focus ghost: a small hollow ring where a predicted moment was asked for (~3 s)
    m.addLayer({
      id: 'apos-ghost',
      type: 'circle',
      source: 'apos-ghost',
      paint: { 'circle-radius': 11, 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': p.ink, 'circle-stroke-width': 2 * heavy, 'circle-stroke-opacity': 0.9 },
    });

    // 3D: elevated aircraft, drop lines and forecast at height (empty in 2D)
    m.addLayer(this.layer3d);

    this.installed = true;
    this.applyViewLayers();
    this.applyHiddenGroups();
    this.liveDirty = true;
    this.overlayDirty = true;
    // re-push everything with the current palette
    this.pushPlaces();
    this.pushMarkers();
    this.pushWorldStatic(true);
    this.pathSig = '';
    this.pushPaths();
    this.pushTraffic();
  }

  private ensureTrailLayers(): void {
    if (!this.installed && !this.map.getSource('apos-trails')) return;
    const m = this.map;
    const beforeId = m.getLayer('apos-rings') ? 'apos-rings' : undefined;
    const wanted = new Set<string>();
    for (const a of this.props.bundle.aircraft) {
      const lid = `apos-trail-${a.id}`;
      wanted.add(lid);
      const rgb = hexToRgb(aircraftColor(a, this.palette));
      if (!m.getLayer(lid)) {
        m.addLayer(
          {
            id: lid,
            type: 'line',
            source: 'apos-trails',
            filter: ['==', ['get', 'id'], a.id],
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-width': this.palette.light ? 2.6 : 2.2,
              'line-opacity': ['case', ['get', 'dim'], 0.45, 1],
              'line-gradient': ['interpolate', ['linear'], ['line-progress'], 0, `rgba(${rgb},0)`, 0.55, `rgba(${rgb},0.28)`, 1, `rgba(${rgb},0.95)`],
            },
          },
          beforeId,
        );
      }
    }
    for (const lid of this.trailLayerIds) if (!wanted.has(lid) && m.getLayer(lid)) m.removeLayer(lid);
    this.trailLayerIds = wanted;
  }

  /** Viewing mode: show / hide whole layer groups (layout visibility + their DOM labels). */
  private applyHiddenGroups(): void {
    const hidden = new Set(this.props.hiddenGroups ?? []);
    const labels: string[] = [];
    for (const g of hidden) labels.push(...GROUP_LAYERS[g].labels);
    this.labels.setHiddenGroups(labels);
    if (!this.installed) return;
    for (const l of this.map.getStyle()?.layers ?? []) {
      if (!l.id.startsWith('apos-')) continue;
      const g = (Object.keys(GROUP_LAYERS) as HiddenGroup[]).find((k) => GROUP_LAYERS[k].layer(l.id));
      if (!g) continue;
      const want = hidden.has(g) ? 'none' : 'visible';
      if (this.map.getLayoutProperty(l.id, 'visibility') !== want) this.map.setLayoutProperty(l.id, 'visibility', want);
    }
  }

  /** 2D vs 3D layer set: the 2D forecast and freshness rings give way to their elevated versions. */
  private applyViewLayers(): void {
    if (!this.installed) return;
    const m = this.map;
    const is3d = this.viewMode === '3d';
    for (const id of [...FORECAST_2D_LAYERS, ...RING_2D_LAYERS]) {
      if (!m.getLayer(id)) continue;
      const want = is3d ? 'none' : 'visible';
      if (m.getLayoutProperty(id, 'visibility') !== want) m.setLayoutProperty(id, 'visibility', want);
    }
    // the ground icon becomes a faint footprint under the elevated glyph (still the tap target)
    if (m.getLayer('apos-ac')) m.setPaintProperty('apos-ac', 'icon-opacity', is3d ? ['*', ['get', 'op'], 0.28] : ['get', 'op']);
  }

  // ───────────── data push ─────────────

  private set(id: SourceId, fc: FC): void {
    this.cache.set(id, fc);
    const src = this.map.getSource(id) as GeoJSONSource | undefined;
    if (src && this.installed) src.setData(fc);
  }

  private indexAircraft(): void {
    this.aircraftById = new Map(this.props.bundle.aircraft.map((a) => [a.id, a]));
    this.aerialCams = new Map();
    for (const c of this.props.bundle.cameras) {
      if (cameraKind(c.mount) !== 'aerial' || !c.aircraftId) continue;
      const list = this.aerialCams.get(c.aircraftId) ?? [];
      list.push(c);
      this.aerialCams.set(c.aircraftId, list);
    }
  }
  private aerialCams = new Map<string, MissionBundle['cameras']>();

  /** Display position of the first Subject aircraft (for FOV wedge reach). */
  private subjectPosition(): LatLon | null {
    for (const a of this.anchors.values()) {
      if (roleKind(this.aircraftById.get(a.state.aircraftId)?.role) === 'subject' && a.state.freshness !== 'lost') return a.disp;
    }
    return null;
  }

  /** Whole-mission flight paths, cut at the world time (Replay playhead / live now). */
  private pushPaths(): void {
    const pr = this.props;
    const obs = pr.pathObs;
    if (!pr.showFlightPath || !obs) {
      if (this.pathSig !== 'off') {
        this.pathSig = 'off';
        this.set('apos-paths', EMPTY);
      }
      return;
    }
    const until = pr.world?.t ?? Number.POSITIVE_INFINITY;
    const p = this.palette;
    const f: GeoJSON.Feature[] = [];
    let sig = '';
    const ids = new Set<string>();
    for (const ac of pr.bundle.aircraft) {
      const samples = obs[ac.id];
      if (!samples?.length) continue;
      ids.add(ac.id);
      const path = this.paths.update(ac.id, samples, pr.pathSince);
      const n = countUpTo(path.t, until);
      sig += `${ac.id}:${n}:${path.t[n - 1] ?? 0}:${path.stepM};`;
      if (n < 2) continue;
      const kind = roleKind(ac.role);
      // a gap in the feed is a gap in the path (never a straight line across hours or days)
      for (const part of splitAtGaps(path.t, path.coords, n)) {
        f.push(
          lineF(part, {
            c: aircraftColor(ac, p),
            w: kind === 'subject' ? (p.light ? 1.8 : 1.6) : 1.2,
            op: kind === 'watch' ? 0.4 : p.light ? 0.6 : 0.5,
          }),
        );
      }
    }
    this.paths.retain(ids);
    sig += `|${p.light}`;
    if (sig === this.pathSig) return;
    this.pathSig = sig;
    this.set('apos-paths', fc(f));
  }

  /** Other traffic: faint arrows, labels at zoom ≥ 10 (always when selected). */
  private pushTraffic(): void {
    const pr = this.props;
    const list = pr.showTraffic && pr.traffic ? pr.traffic : [];
    const p = this.palette;
    const now = pr.world?.t ?? Date.now();
    const sel = pr.selection;
    const f: GeoJSON.Feature[] = [];
    const labels: LabelSpec[] = [];
    const icon = aircraftIconId('arrow', false, p.traffic);
    for (const t of list) {
      const pos = trafficPosition(t, now);
      const selected = isSel(sel, 'traffic', t.hex);
      f.push(point(pos.lon, pos.lat, { kind: 'traffic', id: t.hex, icon, rot: t.trackDeg ?? 0, op: trafficOpacity((now - t.t) / 1000), sel: selected }));
      labels.push({
        id: `tr-${t.hex}`,
        lon: pos.lon,
        lat: pos.lat,
        html: `<span class="num">${esc(trafficLabel(trafficName(t), t.altFt, fmt.altitude))}</span>`,
        className: `ml ml-traffic${selected ? ' is-selected' : ''}`,
        dx: 10,
        dy: -6,
        minZoom: selected ? undefined : 10,
        z: selected ? 8 : 0,
        pinned: selected,
      });
    }
    this.set('apos-traffic', fc(f));
    this.labels.setGroup('traffic', labels);
    if (sel?.kind === 'traffic') this.overlayDirty = true;
  }

  private pushPlaces(): void {
    const p = this.palette;
    const b = this.props.bundle;
    const f: GeoJSON.Feature[] = [];
    const labels: LabelSpec[] = [];
    const sel = this.props.selection;
    for (const a of b.airports) {
      for (const r of a.runways ?? []) {
        if (!r.le || !r.he) continue;
        f.push(poly(runwayPolygon(r.le, r.he, r.widthFt ?? 150), { k: 'rwy', kind: 'airport', id: a.icao }));
        f.push(lineF([[r.le.lon, r.le.lat], [r.he.lon, r.he.lat]], { k: 'rwy-cl' }));
      }
      f.push(point(a.lon, a.lat, { k: 'apt', kind: 'airport', id: a.icao }));
      labels.push({
        id: `apt-${a.icao}`,
        lon: a.lon,
        lat: a.lat,
        html: `<span class="num">${esc(a.icao)}</span>`,
        className: `ml ml-apt${isSel(sel, 'airport', a.icao) ? ' is-selected' : ''}`,
        dx: 8,
        dy: 6,
        minZoom: 8,
        z: 2,
      });
    }
    for (const l of b.locations) {
      const hex = l.kind === 'hazard' ? p.hazard : p.location;
      if (l.radiusM && l.radiusM > 0) f.push(poly(circleRing(l, l.radiusM, 48), { k: 'loc-r', c: hex }));
      f.push(point(l.lon, l.lat, { k: 'loc', kind: 'location', id: l.id, icon: locationIconId(l.kind, hex) }));
      labels.push({
        id: `loc-${l.id}`,
        lon: l.lon,
        lat: l.lat,
        html: esc(l.name),
        className: `ml ml-loc ml-loc--${l.kind}${isSel(sel, 'location', l.id) ? ' is-selected' : ''}`,
        dx: l.kind === 'camera_site' ? 18 : 13,
        dy: -7,
        minZoom: l.kind === 'camera_site' ? 8 : 10,
        z: l.kind === 'camera_site' ? 5 : 1,
        pinned: isSel(sel, 'location', l.id),
      });
    }
    this.set('apos-places', { type: 'FeatureCollection', features: f });
    this.labels.setGroup('places', labels);
  }

  private pushMarkers(): void {
    const p = this.palette;
    const f: GeoJSON.Feature[] = [];
    const labels: LabelSpec[] = [];
    const sel = this.props.selection;
    for (const mk of this.props.markers) {
      if (mk.lat === null || mk.lon === null) continue;
      const hex = p.marker[mk.category] ?? p.ink;
      f.push(point(mk.lon, mk.lat, { kind: 'marker', id: mk.id, icon: flagIconId(hex) }));
      labels.push({
        id: `mk-${mk.id}`,
        lon: mk.lon,
        lat: mk.lat,
        html: `${esc(mk.label)}<span class="num">${esc(fmt.utc(mk.t))}</span>`,
        className: `ml ml-marker ml-marker--${mk.category}${isSel(sel, 'marker', mk.id) ? ' is-selected' : ''}`,
        dx: 12,
        dy: -16,
        minZoom: 11.5,
        z: isSel(sel, 'marker', mk.id) ? 8 : 2,
        pinned: isSel(sel, 'marker', mk.id),
      });
    }
    this.set('apos-markers', { type: 'FeatureCollection', features: f });
    this.labels.setGroup('markers', labels);
  }

  private onWorld(prev: WorldState | null, w: WorldState | null): void {
    const t = now();
    if (!w) {
      this.anchors.clear();
      this.worldT = null;
      this.pushWorldStatic();
      this.liveDirty = true;
      return;
    }
    // world-time rate (≈1 live, N× replay, 0 paused). Backwards / big jumps → snap.
    let snap = false;
    if (this.worldT !== null && prev) {
      const dWorld = w.t - this.worldT;
      const dReal = (t - this.worldPerf) / 1000;
      if (dWorld < 0 || dWorld > 120_000) {
        snap = true;
        this.timeRate = 0;
      } else if (dReal > 0.001) {
        const inst = dWorld / 1000 / dReal;
        this.timeRate = dWorld === 0 ? 0 : Math.max(0, Math.min(64, this.timeRate * 0.5 + inst * 0.5));
      }
    }
    this.worldT = w.t;
    this.worldPerf = t;

    const seen = new Set<string>();
    for (const s of w.aircraft) {
      seen.add(s.aircraftId);
      const old = this.anchors.get(s.aircraftId);
      // Retarget: the display keeps its place and velocity; the new dead-reckoned path takes over and the
      // difference glides out on the springs. Only small corrections (< ~600 m) glide; bigger ones jump.
      let offLat: SpringState = { value: 0, velocity: 0 };
      let offLon: SpringState = { value: 0, velocity: 0 };
      if (old && !snap && !this.reducedMotion) {
        const dLat = old.disp.lat - s.lat;
        const dLon = old.disp.lon - s.lon;
        if (Math.abs(dLat) < GLIDE_MAX_DLAT && Math.abs(dLon) < GLIDE_MAX_DLON) {
          const v = this.drVelocity(s);
          offLat = { value: dLat, velocity: old.drVel.lat + old.offLat.velocity - v.lat };
          offLon = { value: dLon, velocity: old.drVel.lon + old.offLon.velocity - v.lon };
        }
      }
      const rot: SpringState = old && !snap ? old.rot : { value: s.trackDeg ?? 0, velocity: 0 };
      const kind = roleKind(this.aircraftById.get(s.aircraftId)?.role);
      const samples = projectTrack(s, { horizonSec: ribbonHorizonSec(kind), stepSec: 2 });
      this.anchors.set(s.aircraftId, {
        state: s,
        perf: t,
        offLat,
        offLon,
        rot,
        disp: old?.disp ?? { lat: s.lat, lon: s.lon },
        drVel: old?.drVel ?? { lat: 0, lon: 0 },
        ahead: 0,
        samples,
        elev: old?.elev ?? null,
      });
    }
    for (const id of [...this.anchors.keys()]) if (!seen.has(id)) this.anchors.delete(id);

    this.pushTrails(w);
    this.pushWorldStatic();
    this.pushPaths();
    if (this.props.showTraffic && this.props.traffic?.length) this.pushTraffic();
    this.liveDirty = true;
    this.overlayDirty = true;

    if (!this.fittedOnce && (w.aircraft.length > 0 || w.crew.length > 0)) {
      this.fittedOnce = true;
      if (!this.props.followAircraftId) this.fitAll(false, false);
    }
  }

  private pushTrails(w: WorldState): void {
    let sig = '';
    for (const a of w.aircraft) {
      const tr = a.trail;
      sig += `${a.aircraftId}:${tr.length}:${tr[0]?.t ?? 0}:${tr[tr.length - 1]?.t ?? 0}:${a.freshness};`;
    }
    if (sig === this.trailSig) return;
    this.trailSig = sig;
    const f: GeoJSON.Feature[] = [];
    for (const a of w.aircraft) {
      if (a.trail.length < 2) continue;
      const coords: LngLat[] = new Array(a.trail.length);
      for (let i = 0; i < a.trail.length; i++) coords[i] = [a.trail[i]!.lon, a.trail[i]!.lat];
      f.push(lineF(coords, { id: a.aircraftId, dim: a.freshness === 'stale' || a.freshness === 'lost' }));
    }
    this.set('apos-trails', { type: 'FeatureCollection', features: f });
  }

  /** Crew, cameras, FOV, crew accuracy + their labels (world rate). */
  private pushWorldStatic(force = false): void {
    const w = this.props.world;
    const p = this.palette;
    const b = this.props.bundle;
    const sel = this.props.selection;
    const crewF: GeoJSON.Feature[] = [];
    const accF: GeoJSON.Feature[] = [];
    const camF: GeoJSON.Feature[] = [];
    const fovF: GeoJSON.Feature[] = [];
    const crewLabels: LabelSpec[] = [];
    const camLabels: LabelSpec[] = [];
    const acLabels: LabelSpec[] = [];
    if (w) {
      const members = new Map(b.members.map((m) => [m.userId, m]));
      for (const c of w.crew) {
        const m = members.get(c.userId);
        const stale = c.freshness === 'stale' || c.freshness === 'lost';
        const color = stale ? p.crewStale : safeHex(m?.color, p.ink);
        const selected = isSel(sel, 'crew', c.userId);
        crewF.push(point(c.lon, c.lat, { kind: 'crew', id: c.userId, c: color, op: c.freshness === 'lost' ? 0.45 : stale ? 0.7 : 1, sel: selected }));
        if (c.accuracyM && c.accuracyM > 3) accF.push(poly(circleRing(c, Math.min(c.accuracyM, 2000), 40), { c: color }));
        const age = c.freshness === 'live' ? '' : `<span class="ml-age num">${esc(fmt.durationShort(c.ageSec))}</span>`;
        const isMe = c.userId === b.me?.userId;
        crewLabels.push({
          id: `crew-${c.userId}`,
          lon: c.lon,
          lat: c.lat,
          html: `<span class="ml-swatch" style="background:${color}"></span>${isMe ? '<span class="ml-you">YOU</span>' : ''}${esc(m?.callsign || m?.displayName || 'CREW')}${age}`,
          className: `ml ml-crew ml--${c.freshness}${isMe ? ' ml-crew--me' : ''}${selected ? ' is-selected' : ''}`,
          dx: 11,
          dy: -9,
          z: selected ? 8 : 4,
          pinned: selected,
        });
      }
      const cams = new Map(b.cameras.map((c) => [c.id, c]));
      const subject = this.subjectPosition();
      const coverage = new Map((this.props.cameraCoverage ?? []).map((c) => [c.cameraId, c])); // coverage lane
      for (const cs of w.cameras) {
        if (!cs.position) continue;
        const cam = cams.get(cs.cameraId);
        const kind = cameraKind(cam?.mount);
        // Aerial cameras ride with their aircraft as a badge (pushLive), unless the aircraft has no state.
        if (kind === 'aerial' && cam?.aircraftId && w.aircraft.some((a) => a.aircraftId === cam.aircraftId)) continue;
        const selected = isSel(sel, 'camera', cs.cameraId);
        const heading = cs.headingDeg;
        const hfov = cs.hfovDeg ?? fallbackHfov(cam);
        // zoom: the wide end, when it is meaningfully wider than the main (current / long) cone
        const hfovWide = cs.hfovWideDeg != null && hfov != null && cs.hfovWideDeg > hfov + 0.5 ? cs.hfovWideDeg : null;
        const stale = cs.freshness === 'stale' || cs.freshness === 'lost';
        const operator = kind === 'manned' && cam?.operatorUserId ? members.get(cam.operatorUserId) : undefined;
        const color = kind === 'manned' ? safeHex(operator?.color, p.camera) : p.camera;
        camF.push(
          point(cs.position.lon, cs.position.lat, {
            kind: 'camera',
            id: cs.cameraId,
            icon: cameraIconId(kind === 'manned' ? 'manned' : kind === 'aerial' ? 'aerial' : 'static', color),
            // static: the lens points along the heading; manned: an upright badge beside its operator
            rot: kind === 'static' && heading !== null ? heading - 90 : 0,
            off: kind === 'manned' ? [-15, -13] : [0, 0],
            op: stale ? 0.6 : 1,
          }),
        );
        // coverage lane: in frame → active cone. Manned (round 4): along the operator's live aim; with the
        // aim unknown, a dashed cone toward the aircraft whenever coverage knows where it is.
        const cov = coverage.get(cs.cameraId);
        const active = shared.coveragePresence(cov) === true;
        const aimUnknown = kind === 'manned' && cs.aim !== 'live';
        const aim = aimUnknown ? (cov?.aimDeg ?? null) : heading;
        if (aim !== null && hfov && kind !== 'aerial') {
          const range = wedgeRangeM(cs.position, subject, b.mission.center, { headingDeg: aim, hfovDeg: hfovWide ?? hfov });
          const props = { rec: cs.recording, sel: selected, faint: aimUnknown, act: active, tier: 'main' };
          if (hfovWide != null) fovF.push(poly(wedge(cs.position, aim, hfovWide, range), { ...props, act: false, tier: 'wide' }));
          fovF.push(poly(wedge(cs.position, aim, hfov, range), props));
        }
        const focal = cs.focalLengthMm ?? cam?.focalLengthMm ?? null;
        const aimTag = aimUnknown && aim !== null ? '<span class="ml-aim">aim unknown</span>' : '';
        camLabels.push({
          id: `cam-${cs.cameraId}`,
          lon: cs.position.lon,
          lat: cs.position.lat,
          html: `${esc(cameraMapLabel(cam, focal))}${cs.recording ? '<span class="ml-rec">REC</span>' : ''}${active ? '<span class="ml-inframe">IN FRAME</span>' : ''}${aimTag}`,
          className: `ml ml-cam ml-cam--${kind}${selected ? ' is-selected' : ''}${stale ? ' ml--stale' : ''}${active ? ' ml-cam--active' : ''}`,
          // manned: under the operator's own label; static: under a camera-site name at the same spot
          dx: kind === 'manned' ? 11 : 14,
          dy: kind === 'manned' ? 6 : 10,
          // a static camera is what the crew placed there: it outranks the place name
          z: selected ? 8 : kind === 'static' ? 6 : cs.recording ? 4 : 3,
          pinned: selected,
        });
      }
      for (const a of w.aircraft) {
        const ac = this.aircraftById.get(a.aircraftId);
        const selected = isSel(sel, 'aircraft', a.aircraftId);
        const status =
          a.freshness !== 'live'
            ? `<span class="ml-status ml-status--${a.freshness}">${esc(fmt.freshness(a.freshness).toUpperCase())} ${esc(fmt.durationShort(a.ageSec))}</span>`
            : a.quality === 'estimated' && a.ageSec > EST_VISUAL_SEC
              ? '<span class="ml-status ml-status--est">EST</span>'
              : '';
        const kind = roleKind(ac?.role);
        const style = roleStyle(ac);
        if (this.props.quietLost && a.freshness === 'lost') {
          acLabels.push({
            id: `ac-${a.aircraftId}`, lon: a.lon, lat: a.lat,
            html: `<span class="ml-ac__name">${esc(ac?.label ?? 'AIRCRAFT')}</span><span class="ml-status ml-status--idle">LAST SEEN ${esc(sinceText(a.ageSec * 1000).toUpperCase())}</span>`,
            className: `ml ml-ac ml-ac--${kind} ml--${a.freshness}${selected ? ' is-selected' : ''}`,
            dx: Math.round(8 + 14 * style.iconSize), dy: -14, z: selected ? 20 : 10, pinned: true,
          });
          continue;
        }
        const alt = a.altFt === null ? '— ft' : fmt.altitude(Math.round(a.altFt / 10) * 10);
        const name = esc(ac?.label ?? 'AIRCRAFT');
        const word = this.props.aircraftStatus?.[a.aircraftId];
        const phase = word && kind !== 'watch' ? `<span class="ml-ac__phase">${esc(word)}</span>` : '';
        let html: string;
        if (style.label === 'full' || selected) {
          html = `<span class="ml-ac__name">${name}</span>${phase}<span class="ml-ac__data num">${esc(alt)} · ${esc(fmt.speed(a.gsKt === null ? null : Math.round(a.gsKt)))}</span>`;
        } else if (style.label === 'medium') {
          html = `<span class="ml-ac__name">${name}</span>${phase}<span class="ml-ac__data num">${esc(alt)}</span>`;
        } else {
          html = `<span class="ml-ac__name">${esc(ac?.registration?.trim() || ac?.label || 'AIRCRAFT')}<span class="ml-ac__data num"> · ${esc(alt)}</span></span>`;
        }
        // 3D: the height the glyph is drawn at, in words ("~" while the position is estimated)
        const hollow = (a.quality === 'estimated' && a.ageSec > EST_VISUAL_SEC) || a.quality === 'predicted';
        const height = this.viewMode === '3d' ? heightLabel(a, hollow) : null;
        if (height) html += `<span class="ml-ac__agl num">${esc(height)}</span>`;
        const anchor = this.anchors.get(a.aircraftId);
        acLabels.push({
          id: `ac-${a.aircraftId}`,
          lon: anchor?.disp.lon ?? a.lon,
          lat: anchor?.disp.lat ?? a.lat,
          elevM: this.viewMode === '3d' ? (anchor?.elev ?? null) : null,
          html: html + status,
          className: `ml ml-ac ml-ac--${kind} ml--${a.freshness}${selected ? ' is-selected' : ''}`,
          dx: Math.round(8 + 14 * style.iconSize),
          dy: kind === 'watch' && !selected ? -8 : -14,
          z: selected ? 20 : kind === 'subject' ? 10 : kind === 'chase' ? 9 : 7,
          // Subject and Chase always keep their tag; Watch tags give way in a crowd
          pinned: selected || kind !== 'watch',
        });
      }
    }
    this.set('apos-crew', fc(crewF));
    this.set('apos-crew-acc', fc(accF));
    this.set('apos-cams', fc(camF));
    this.set('apos-fov', fc(fovF));
    this.labels.setGroup('crew', crewLabels);
    this.labels.setGroup('cams', camLabels);
    this.labels.setGroup('aircraft', acLabels);
    this.pushBeam();
    // labels of places/markers also carry selection styling
    if (force || sel !== this.lastSelForPlaces) {
      this.lastSelForPlaces = sel;
      this.pushPlaces();
      this.pushMarkers();
    }
  }
  private lastSelForPlaces: SelectionRef | null = null;

  // ───────────── per-frame ─────────────

  private frame = () => {
    if (this.destroyed) return;
    this.raf = requestAnimationFrame(this.frame);
    const t = now();
    const dt = Math.min(0.1, (t - this.lastFrame) / 1000);
    this.lastFrame = t;
    if (!this.installed) {
      this.labels.render();
      return;
    }
    if (this.ghost && t > this.ghost.until) {
      this.ghost = null;
      this.pushGhost();
      this.liveDirty = true;
    }
    this.reconcileFlat();
    const moving = this.anchors.size > 0 && (this.timeRate > 0 || this.hasActiveCorrection());
    if (moving || this.liveDirty) {
      this.liveDirty = false;
      this.pushLive(t, dt);
      this.follow(dt);
      // overlays anchored to moving things: ≤ 20 Hz
      if (this.overlayDirty || t - this.lastOverlayPerf > 50) this.pushOverlays(t);
    } else if (this.overlayDirty) {
      this.pushOverlays(t);
    }
    this.pushEdge(t);
    this.labels.render();
  };

  /**
   * Off-screen arrow for the find aircraft (≤ 8 Hz, emitted on change): where the line from the
   * centre of the padded view to the aircraft leaves it, inset so a pill fits there.
   */
  private pushEdge(t: number): void {
    if (!this.cb.onEdge || t - this.lastEdgePerf < 125) return;
    this.lastEdgePerf = t;
    const id = this.props.findAircraftId;
    const a = id ? this.anchors.get(id) : undefined;
    let info: EdgeInfo | null = null;
    if (a && a.state.freshness !== 'lost') {
      const c = this.map.getContainer();
      const w = c.clientWidth;
      const h = c.clientHeight;
      const pad = this.props.padding;
      const p = this.map.project([a.disp.lon, a.disp.lat]);
      // on screen = anywhere across the width (the side controls are small), between the top
      // overlays and the bottom sheet
      const l = 4;
      const r = w - 4;
      const top = pad.top + 4;
      const b = h - pad.bottom - 4;
      if (r > l && b > top && (p.x < l || p.x > r || p.y < top || p.y > b)) {
        // where the ray from the view centre leaves the rect (top edge below the chips)
        const at = Math.min(top + 72, (top + b) / 2);
        const cx = (l + r) / 2;
        const cy = (at + b) / 2;
        const dx = p.x - cx;
        const dy = p.y - cy;
        const kx = dx > 0 ? (r - cx) / dx : dx < 0 ? (l - cx) / dx : Infinity;
        const ky = dy > 0 ? (b - cy) / dy : dy < 0 ? (at - cy) / dy : Infinity;
        const k = Math.min(kx, ky, 1);
        const angleDeg = Math.round((Math.atan2(dx, -dy) * 180) / Math.PI);
        if (kx <= ky) {
          // left / right edge: the pill hugs that edge
          info = { x: dx > 0 ? r - 4 : l + 4, y: Math.round(Math.min(b - 84, Math.max(at, cy + dy * k))), angleDeg, side: dx > 0 ? 'right' : 'left' };
        } else {
          info = { x: Math.round(Math.min(r - 84, Math.max(l + 84, cx + dx * k))), y: Math.round(dy > 0 ? b - 84 : at), angleDeg, side: dy > 0 ? 'bottom' : 'top' }; // bottom: above the thumb cluster
        }
      }
    }
    const sig = info ? `${info.side}|${info.x}|${info.y}|${Math.round(info.angleDeg / 3)}` : '';
    if (sig === this.edgeSig) return;
    this.edgeSig = sig;
    this.cb.onEdge(info);
  }

  /** A spring (position offset or heading) is still gliding. */
  private hasActiveCorrection(): boolean {
    for (const a of this.anchors.values()) {
      if (Math.abs(a.offLat.value) > SETTLED_DEG || Math.abs(a.offLon.value) > SETTLED_DEG) return true;
      if (Math.abs(a.offLat.velocity) > SETTLED_DEG || Math.abs(a.offLon.velocity) > SETTLED_DEG) return true;
      const tr = a.state.trackDeg;
      if (tr !== null && (Math.abs(angleDelta(a.rot.value, tr)) > SETTLED_ROT || Math.abs(a.rot.velocity) > SETTLED_ROT)) return true;
    }
    return false;
  }

  /** Whether the display dead-reckons this state between world updates (replay pause / stale → no). */
  private dreckons(s: AircraftState): boolean {
    return this.timeRate > 0 && !!s.gsKt && s.trackDeg !== null && s.freshness !== 'lost' && s.ageSec < MAX_EXTRAPOLATE_SEC;
  }

  /** Dead-reckoning velocity (deg/s of lat/lon) of a state at the current world-time rate. */
  private drVelocity(s: AircraftState): { lat: number; lon: number } {
    if (!this.dreckons(s)) return { lat: 0, lon: 0 };
    const v = s.gsKt! * KT_TO_MPS * this.timeRate;
    const tr = (s.trackDeg! * Math.PI) / 180;
    return { lat: (v * Math.cos(tr)) / 111_320, lon: (v * Math.sin(tr)) / (111_320 * Math.max(0.01, Math.cos((s.lat * Math.PI) / 180))) };
  }

  /**
   * Display position for an aircraft at perf time t: dead reckoning (gated by the world-time rate, so a
   * paused replay holds still) plus the spring offset. The spring is critically damped with a 0.4 s
   * response: its steady lag behind a moving target is response/π ≈ 0.13 s, inside the 250 ms rule.
   */
  private displayOf(a: Anchor, t: number, dt: number): { lat: number; lon: number } {
    const s = a.state;
    let lat = s.lat;
    let lon = s.lon;
    a.ahead = 0;
    a.drVel = { lat: 0, lon: 0 };
    // keep gliding between world updates for as long as the world itself dead-reckons (no stepping when stale)
    if (this.dreckons(s)) {
      const raw = (this.timeRate * (t - a.perf)) / 1000;
      const ahead = Math.min(MAX_AHEAD_S, raw);
      if (ahead > 0) {
        const d = destination(lat, lon, s.trackDeg!, s.gsKt! * KT_TO_MPS * ahead);
        lat = d.lat;
        lon = d.lon;
        a.ahead = ahead;
        if (raw < MAX_AHEAD_S) a.drVel = this.drVelocity(s);
      }
    }
    const opts = { response: this.reducedMotion ? 0 : GLIDE_RESPONSE_SEC };
    a.offLat = stepSpring(a.offLat, 0, dt, opts);
    a.offLon = stepSpring(a.offLon, 0, dt, opts);
    if (s.trackDeg !== null) a.rot = stepAngleSpring(a.rot, s.trackDeg, dt, opts);
    a.disp = { lat: lat + a.offLat.value, lon: lon + a.offLon.value };
    return a.disp;
  }

  /** My facing cone: this phone's compass heading on my own crew dot. */
  private pushBeam(): void {
    const h = this.props.myHeadingDeg;
    const me = this.props.bundle.me?.userId;
    const c = h != null && me ? this.props.world?.crew.find((x) => x.userId === me) : null;
    if (!c || h == null) {
      this.set('apos-beam', EMPTY);
      return;
    }
    const m = this.props.bundle.members.find((x) => x.userId === me);
    const hex = safeHex(m?.color, this.palette.ink);
    this.set('apos-beam', fc([point(c.lon, c.lat, { icon: beamIconId(hex), rot: h })]));
  }

  private pushLive(t: number, dt: number): void {
    const p = this.palette;
    const f: GeoJSON.Feature[] = [];
    const sel = this.props.selection;
    const is3d = this.viewMode === '3d';
    const mesh: Mesh | null = is3d ? emptyMesh() : null;
    const mpp = metersPerPixel(this.map.getCenter().lat, this.map.getZoom());
    let z = 0;
    for (const a of this.anchors.values()) {
      const s = a.state;
      const ac = this.aircraftById.get(s.aircraftId);
      const color = aircraftColor(ac, p);
      const pos = this.displayOf(a, t, dt);
      // between 1 Hz samples every live state is briefly 'estimated'; only show it once it matters
      const hollow = (s.quality === 'estimated' && s.ageSec > EST_VISUAL_SEC) || s.quality === 'predicted';
      const selected = isSel(sel, 'aircraft', s.aircraftId);
      const ring = p.freshness[s.freshness];
      const style = roleStyle(ac);
      const rot = s.trackDeg !== null ? a.rot.value : 0;
      const props: Record<string, unknown> = {
        k: 'ac',
        kind: 'aircraft',
        id: s.aircraftId,
        icon: aircraftIconId(aircraftShape(ac), hollow, color),
        rot,
        op: s.freshness === 'live' ? 1 : s.freshness === 'aging' ? 0.9 : s.freshness === 'stale' ? 0.6 : this.props.quietLost ? 0.85 : 0.4,
        sel: selected,
        sz: style.iconSize,
        rr: style.ringR,
        // Subject on top, then Chase, then Watch (stable within a role)
        z: selected ? 1000 : style.z * 10 + (z++ % 10),
      };
      const showRing = !!ring && !(this.props.quietLost && s.freshness === 'lost');
      if (showRing) props.ring = ring;
      f.push(point(pos.lon, pos.lat, props));
      // head: last trail point → display position (dashed when dead-reckoned)
      const last = s.trail[s.trail.length - 1];
      if (last && (last.lat !== pos.lat || last.lon !== pos.lon)) {
        f.push(lineF([[last.lon, last.lat], [pos.lon, pos.lat]], { k: 'head', c: color, est: s.quality === 'estimated' && s.ageSec > EST_VISUAL_SEC }));
      }
      if (s.estimateM && s.ageSec > EST_VISUAL_SEC && s.estimateM > 40) {
        f.push(poly(circleRing(pos, s.estimateM, 40), { k: 'est', c: color }));
      }
      // where it's heading: the forecast (class E) from the shared constant-velocity model — 2 min for
      // Subject/Chase (1 min for Watch), ticks at 10/20/30/60 s; nothing when the position is stale/lost
      const wantForecast = this.props.showLeaders && !!s.gsKt && s.gsKt > RIBBON_MIN_GS_KT && s.trackDeg !== null && s.freshness !== 'lost' && a.samples.length > 1;
      if (!is3d && wantForecast) f.push(...trackFeatures(s.aircraftId, rebaseSamples(a.samples, a.ahead, pos), color));
      a.elev = null;
      const fcId = `fc-${s.aircraftId}`;
      let fcLive = false;
      if (mesh) {
        const h = this.heightsOf(s, pos);
        const c = rgbOf(color);
        const ink = rgbOf(p.ink);
        if (wantForecast) {
          // The forecast ribbon / cone / ticks only change with the world state, the zoom, the height, the
          // colour or a quarter second of dead reckoning: rebuild then, and otherwise just slide the batch to
          // the (spring-smoothed) display position.
          const key = { owner: a, aheadQ: quantizeAhead(a.ahead), mpp, air: h.air, ground: h.ground, color, light: p.light };
          if (this.fcGate.stale(fcId, key)) {
            const fm = emptyMesh();
            forecastMesh(fm, rebaseSamples(a.samples, key.aheadQ, pos), h.air, h.ground, mpp, c, p.light);
            this.layer3d.setChunk(fcId, fm.pos.length > 0 ? fm : null, { lng: pos.lon, lat: pos.lat });
          }
          this.layer3d.moveChunk(fcId, pos.lon, pos.lat);
          fcLive = true;
        }
        if (h.air !== null) {
          a.elev = h.air;
          f.push(point(pos.lon, pos.lat, { k: 'shadow', r: Math.round(9 * style.iconSize) }));
          dropLineMesh(mesh, pos.lon, pos.lat, h.ground, h.air, mpp * 0.8, ink, 0.75);
          const sizeM = mpp * 15 * style.iconSize * (selected ? 1.12 : 1);
          if (showRing) ringMesh(mesh, pos.lon, pos.lat, h.air, mpp * style.ringR, mpp * 2, rgbOf(ring!), 0.9);
          if (selected) ringMesh(mesh, pos.lon, pos.lat, h.air, mpp * 24, mpp * 1.5, rgbOf(p.halo), 0.9);
          const opacity = Number(props.op);
          glyphMesh(mesh, pos.lon, pos.lat, h.air, rot, sizeM, c, hollow ? opacity * 0.55 : opacity, rgbOf(p.glyphOutline));
        }
      }
      if (fcLive) this.fcSeen.add(fcId);
      this.labels.move('aircraft', `ac-${s.aircraftId}`, pos.lon, pos.lat, a.elev);
      // Aerial cameras: a small badge riding with the aircraft (upper-left of the icon)
      for (const cam of this.aerialCams.get(s.aircraftId) ?? []) {
        const off = -Math.round(10 + 9 * style.iconSize);
        f.push(point(pos.lon, pos.lat, { k: 'acam', kind: 'camera', id: cam.id, icon: cameraIconId('aerial', p.camera), off: [off, off] }));
      }
    }
    if (mesh && this.ghost) {
      const g = this.ghost;
      const ground = this.terrainStatus === 'on' ? (this.map.queryTerrainElevation([g.lon, g.lat]) ?? 0) : 0;
      const zg = g.altM === null ? ground : this.terrainStatus === 'on' ? g.altM : g.altM - this.flatGroundRef();
      ringMesh(mesh, g.lon, g.lat, Math.max(ground, zg), mpp * 11, mpp * 2, rgbOf(p.ink), 0.9);
      if (zg > ground) dropLineMesh(mesh, g.lon, g.lat, ground, zg, mpp * 0.6, rgbOf(p.ink), 0.45);
    }
    if (mesh) {
      this.layer3d.pruneChunks(this.fcSeen);
      this.fcGate.retain(this.fcSeen);
      this.layer3d.setMesh(mesh);
    } else if (this.meshShown) {
      this.layer3d.clear();
      this.fcGate.clear();
    }
    this.fcSeen.clear();
    this.meshShown = !!mesh;
    this.set('apos-live', fc(f));
    this.emitForecastOff();
  }
  private meshShown = false;
  private fcGate = new ForecastGate();
  private fcSeen = new Set<string>();

  /**
   * 3D render heights (m) at the displayed position. With terrain: the DEM under it, and the aircraft at
   * ground + AGL (else its MSL altitude). Flat: ground 0 and the aircraft at its AGL (else MSL — the label
   * then says MSL, never AGL). null air = altitude unknown.
   */
  private heightsOf(s: AircraftState, pos: LatLon): { ground: number; air: number | null } {
    const terrainOn = this.terrainStatus === 'on';
    const q = terrainOn ? this.map.queryTerrainElevation([pos.lon, pos.lat]) : null;
    const ground = terrainOn ? (q ?? s.groundElevM ?? 0) : 0;
    const msl = mslMetres(s);
    const agl = aglMetres(s);
    if (agl !== null) return { ground, air: ground + Math.max(0, agl) };
    if (msl !== null) return { ground, air: terrainOn ? Math.max(ground, msl) : Math.max(0, msl) };
    return { ground, air: null };
  }

  /** Flat 3D: the ground under the aircraft (for heights given in MSL, e.g. a focus point). */
  private flatGroundRef(): number {
    for (const a of this.anchors.values()) if (a.state.groundElevM != null && Number.isFinite(a.state.groundElevM)) return a.state.groundElevM;
    return 0;
  }

  /** Focus aircraft for the forecast chip: selected → followed → first Subject. */
  private emitForecastOff(): void {
    let a: Anchor | undefined;
    const sel = this.props.selection;
    if (sel?.kind === 'aircraft') a = this.anchors.get(sel.id);
    if (!a && this.props.followAircraftId) a = this.anchors.get(this.props.followAircraftId);
    if (!a) for (const x of this.anchors.values()) if (roleKind(this.aircraftById.get(x.state.aircraftId)?.role) === 'subject') { a = x; break; }
    const text = this.props.showLeaders && a ? forecastOffText(a.state) : null;
    if (text === this.forecastOff) return;
    this.forecastOff = text;
    this.cb.onForecastOff?.(text);
  }

  private pushGhost(): void {
    const g = this.ghost;
    this.set('apos-ghost', g && this.viewMode === '2d' ? fc([point(g.lon, g.lat, { k: 'ghost' })]) : EMPTY);
  }

  /** Back in 2D: once the camera is level, clamp pitch to 0 again (exactly the old 2D map). */
  private reconcileFlat(): void {
    if (this.viewMode !== '2d' || this.map.getMaxPitch() === 0) return;
    if (this.map.isMoving() || this.map.isEasing()) return;
    if (this.map.getPitch() > 0.01) {
      // an interrupted transition: level out (follow() eases the pitch itself while following)
      if (!this.props.followAircraftId || !this.anchors.has(this.props.followAircraftId)) this.map.jumpTo({ pitch: 0 });
      return;
    }
    this.map.setMaxPitch(0);
  }

  private follow(dt: number): void {
    const id = this.props.followAircraftId;
    if (!id) return;
    const a = this.anchors.get(id);
    if (!a) return;
    const c = this.map.getCenter();
    const snap = this.reducedMotion;
    const k = snap ? 1 : 1 - Math.exp(-dt / FOLLOW_TAU.center);
    const lat = c.lat + (a.disp.lat - c.lat) * k;
    const lon = c.lng + (a.disp.lon - c.lng) * k;
    const opts: maplibregl.JumpToOptions = { center: [lon, lat] };
    const is3d = this.viewMode === '3d';
    // Chase (3D): bearing follows the smoothed track whatever track-up says; 2D keeps the track-up switch
    if ((this.props.trackUp || is3d) && a.state.trackDeg !== null) {
      const target = a.rot.value;
      this.followBearing = this.map.getBearing();
      opts.bearing = this.followBearing + angleDelta(this.followBearing, target) * (snap ? 1 : 1 - Math.exp(-dt / FOLLOW_TAU.bearing));
    }
    // pitch: chase pitch in 3D (user-adjustable by pitching), level in 2D; same smoothing family (τ 0.35 s)
    const pitchTarget = is3d ? this.chasePitch : 0;
    const pitch = this.map.getPitch();
    if (Math.abs(pitch - pitchTarget) > 0.01) opts.pitch = pitch + (pitchTarget - pitch) * (snap ? 1 : 1 - Math.exp(-dt / FOLLOW_TAU.pitch));
    this.map.jumpTo(opts);
  }

  /** Selection halo + rings, pairs, sun ray. */
  private pushOverlays(t: number): void {
    this.overlayDirty = false;
    this.lastOverlayPerf = t;
    const p = this.palette;
    const sel = this.props.selection;
    const selF: GeoJSON.Feature[] = [];
    const ringLabels: LabelSpec[] = [];
    const selPos = sel ? this.positionOf(sel) : null;
    if (sel && selPos) {
      selF.push(point(selPos.lon, selPos.lat, { k: 'halo', r: sel.kind === 'aircraft' ? 24 : sel.kind === 'location' ? 18 : 14 }));
      if (this.props.showRangeRings && sel.kind !== 'traffic') {
        for (const nm of [1, 2, 5]) {
          selF.push(lineF(circleRing(selPos, nm * NM, 96), { k: 'ring' }));
          const lp = destination(selPos.lat, selPos.lon, 135, nm * NM);
          ringLabels.push({ id: `ring-${nm}`, lon: lp.lon, lat: lp.lat, html: `<span class="num">${nm} NM</span>`, className: 'ml ml-ring', dx: 3, dy: 3, z: 0 });
        }
      }
    }
    this.set('apos-sel', fc(selF));
    this.labels.setGroup('rings', ringLabels);

    // pairs
    const pairF: GeoJSON.Feature[] = [];
    const pairLabels: LabelSpec[] = [];
    this.props.pairs.forEach((pair, i) => {
      const a = this.kinematicOf(pair.from);
      const b = this.kinematicOf(pair.to);
      if (!a || !b) return;
      pairF.push(lineF([[a.pos.lon, a.pos.lat], [b.pos.lon, b.pos.lat]], {}));
      const mid = midpoint(a.pos, b.pos);
      // On phone-width maps the bottom sheet already shows these numbers; the label only covers aircraft.
      if (this.map.getContainer().clientWidth >= 600) {
        pairLabels.push({ id: `pair-${i}`, lon: mid.lon, lat: mid.lat, html: pairLabel(a, b), className: 'ml ml-pair', anchor: 'center', z: 15, pinned: true });
      }
    });
    this.set('apos-pairs', fc(pairF));
    this.labels.setGroup('pairs', pairLabels);

    // sun ray
    const sunF: GeoJSON.Feature[] = [];
    const sunLabels: LabelSpec[] = [];
    const w = this.props.world;
    // hidden after civil twilight (sun below −6°): at night it's just clutter; dashed while below the horizon
    if (this.props.showSunRay && w?.sun && Number.isFinite(w.sun.azimuthDeg) && w.sun.elevationDeg > -6) {
      const anchor = selPos ?? (this.props.sunRayFrom ? this.positionOf(this.props.sunRayFrom) : null) ?? this.props.bundle.mission.center;
      const mpp = metersPerPixel(anchor.lat, this.map.getZoom());
      const below = w.sun.elevationDeg < 0;
      const az = w.sun.azimuthDeg;
      const end = destination(anchor.lat, anchor.lon, az, 150 * mpp);
      sunF.push(lineF([[anchor.lon, anchor.lat], [end.lon, end.lat]], { k: 'ray', below }));
      sunF.push(point(end.lon, end.lat, { k: 'end' }));
      if (!below) {
        // shadow direction: short tick opposite the sun with a cross-bar
        const sh = destination(anchor.lat, anchor.lon, az + 180, 34 * mpp);
        const b1 = destination(sh.lat, sh.lon, az + 90, 6 * mpp);
        const b2 = destination(sh.lat, sh.lon, az - 90, 6 * mpp);
        sunF.push(lineF([[anchor.lon, anchor.lat], [sh.lon, sh.lat]], { k: 'shadow' }));
        sunF.push(lineF([[b1.lon, b1.lat], [b2.lon, b2.lat]], { k: 'shadow' }));
      }
      sunLabels.push({
        id: 'sun',
        lon: end.lon,
        lat: end.lat,
        // plain words: "SUN W · 12° up" (the exact azimuth is in the Sun panel)
        html: `SUN ${esc(shared.compassPoint(az))} <span class="num">· ${Math.abs(w.sun.elevationDeg).toFixed(0)}° ${w.sun.elevationDeg >= 0 ? 'up' : 'below'}</span>`,
        className: `ml ml-sun${below ? ' is-below' : ''}`,
        // push the label beyond the ray's end, on the side it points to
        dx: Math.sin((az * Math.PI) / 180) * 18,
        dy: -Math.cos((az * Math.PI) / 180) * 12 - 7,
        anchor: az > 180 ? 'right' : 'left',
        z: 6,
      });
    }
    this.set('apos-sun', fc(sunF));
    this.labels.setGroup('sun', sunLabels);
    void p;
  }

  private positionOf(ref: SelectionRef): LatLon | null {
    const w = this.props.world;
    const b = this.props.bundle;
    switch (ref.kind) {
      case 'aircraft': {
        const a = this.anchors.get(ref.id);
        return a ? a.disp : null;
      }
      case 'crew': {
        const c = w?.crew.find((x) => x.userId === ref.id);
        return c ? { lat: c.lat, lon: c.lon } : null;
      }
      case 'camera': {
        const c = w?.cameras.find((x) => x.cameraId === ref.id);
        if (c?.position) return c.position;
        const cam = b.cameras.find((x) => x.id === ref.id);
        return cam?.position ?? null;
      }
      case 'location': {
        const l = b.locations.find((x) => x.id === ref.id);
        return l ? { lat: l.lat, lon: l.lon } : null;
      }
      case 'airport': {
        const a: Airport | undefined = b.airports.find((x) => x.icao === ref.id);
        return a ? { lat: a.lat, lon: a.lon } : null;
      }
      case 'marker': {
        const m = this.props.markers.find((x) => x.id === ref.id);
        return m && m.lat !== null && m.lon !== null ? { lat: m.lat, lon: m.lon } : null;
      }
      case 'traffic': {
        const t = this.props.showTraffic ? this.props.traffic?.find((x) => x.hex === ref.id) : undefined;
        return t ? trafficPosition(t, w?.t ?? Date.now()) : null;
      }
    }
  }

  private kinematicOf(ref: TargetRef): Kin | null {
    const pos = this.positionOf(ref);
    if (!pos) return null;
    const w = this.props.world;
    if (ref.kind === 'aircraft') {
      const s = this.anchors.get(ref.id)?.state;
      return { pos, altFt: s?.altFt ?? null, gsKt: s?.gsKt ?? null, trackDeg: s?.trackDeg ?? null, vsFpm: s?.vsFpm ?? null };
    }
    if (ref.kind === 'crew') {
      const c = w?.crew.find((x) => x.userId === ref.id);
      return { pos, altFt: null, gsKt: c?.speedMps ? c.speedMps / KT_TO_MPS : 0, trackDeg: c?.headingDeg ?? null, vsFpm: null };
    }
    if (ref.kind === 'airport') {
      const a = this.props.bundle.airports.find((x) => x.icao === ref.id);
      return { pos, altFt: a?.elevFt ?? null, gsKt: 0, trackDeg: null, vsFpm: null };
    }
    if (ref.kind === 'location') {
      const l = this.props.bundle.locations.find((x) => x.id === ref.id);
      return { pos, altFt: l?.elevM !== null && l?.elevM !== undefined ? l.elevM / 0.3048 : null, gsKt: 0, trackDeg: null, vsFpm: null };
    }
    return { pos, altFt: null, gsKt: 0, trackDeg: null, vsFpm: null };
  }

  // ───────────── interaction ─────────────

  private onMove = () => {
    this.labels.markDirty();
    if (this.props.showSunRay) this.overlayDirty = true;
    if (this.viewMode === '3d') this.liveDirty = true; // glyph and line widths are sized per pixel
  };

  private onDragStart = (e: { originalEvent?: unknown }) => {
    if (e.originalEvent && this.props.followAircraftId) this.cb.onFollowBreak?.();
  };

  /** 3D chase: a user pitch gesture sets the chase pitch instead of fighting it. */
  private onPitchEnd = (e: { originalEvent?: unknown }) => {
    if (e.originalEvent && this.viewMode === '3d') this.chasePitch = Math.min(MAX_PITCH_3D, this.map.getPitch());
  };

  /** 3D: elevated labels project with the camera of the frame just drawn. */
  private onRender = () => {
    if (this.viewMode === '3d') this.labels.markDirty();
  };

  private onMotionPref = (e: MediaQueryListEvent) => {
    this.reducedMotion = e.matches;
  };

  private pick(e: MapMouseEvent): { kind: string; id: string } | null {
    const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
    const r = coarse ? 22 : 12;
    const layers = CLICK_LAYERS.filter((l) => this.map.getLayer(l));
    if (!layers.length) return null;
    const feats = this.map.queryRenderedFeatures(
      [
        [e.point.x - r, e.point.y - r],
        [e.point.x + r, e.point.y + r],
      ],
      { layers },
    );
    let best: { kind: string; id: string; score: number } | null = null;
    // 3D: the elevated glyph is a tap target too (the ground footprint stays one through the layer)
    if (this.viewMode === '3d') {
      for (const a of this.anchors.values()) {
        if (a.elev === null) continue;
        const pt = this.layer3d.project(a.disp.lon, a.disp.lat, a.elev);
        if (!pt) continue;
        const d = Math.hypot(pt.x - e.point.x, pt.y - e.point.y);
        if (d <= r * 1.5 && (!best || d < best.score)) best = { kind: 'aircraft', id: a.state.aircraftId, score: d };
      }
    }
    for (const f of feats) {
      const kind = f.properties?.kind as string | undefined;
      const id = f.properties?.id as string | undefined;
      if (!kind || !id || f.geometry.type !== 'Point') continue;
      const [lon, lat] = f.geometry.coordinates as [number, number];
      const pt = this.map.project([lon, lat]);
      const d = Math.hypot(pt.x - e.point.x, pt.y - e.point.y);
      const score = d + (CLICK_PRIORITY[kind] ?? 9) * 4;
      if (!best || score < best.score) best = { kind, id, score };
    }
    return best ? { kind: best.kind, id: best.id } : null;
  }

  private onClick = (e: MapMouseEvent) => {
    const hit = this.pick(e);
    if (hit) {
      this.cb.onSelect({ kind: hit.kind, id: hit.id } as SelectionRef);
      return;
    }
    if (this.cb.onMapClick) {
      this.cb.onMapClick({ lat: e.lngLat.lat, lon: e.lngLat.lng });
      return;
    }
    this.cb.onSelect(null);
  };

  private onHover = (e: MapMouseEvent) => {
    if (this.hoverRaf) return;
    this.hoverRaf = requestAnimationFrame(() => {
      this.hoverRaf = 0;
      if (this.destroyed) return;
      const hit = this.pick(e);
      this.map.getCanvas().style.cursor = hit ? 'pointer' : this.cb.onMapClick ? 'crosshair' : '';
    });
  };
}

// ───────────── helpers ─────────────

interface Kin {
  pos: LatLon;
  altFt: number | null;
  gsKt: number | null;
  trackDeg: number | null;
  vsFpm: number | null;
}

function isSel(sel: SelectionRef | null, kind: string, id: string): boolean {
  return !!sel && sel.kind === kind && sel.id === id;
}

function point(lon: number, lat: number, props: Record<string, unknown>): GeoJSON.Feature {
  return { type: 'Feature', properties: props, geometry: { type: 'Point', coordinates: [lon, lat] } };
}
function lineF(coords: LngLat[], props: Record<string, unknown>): GeoJSON.Feature {
  return { type: 'Feature', properties: props, geometry: { type: 'LineString', coordinates: coords } };
}
function poly(ring: LngLat[], props: Record<string, unknown>): GeoJSON.Feature {
  return { type: 'Feature', properties: props, geometry: { type: 'Polygon', coordinates: [ring] } };
}
function fc(features: GeoJSON.Feature[]): FC {
  return { type: 'FeatureCollection', features };
}

function wedge(o: LatLon, heading: number, hfov: number, rangeM: number): LngLat[] {
  try {
    const r = shared.fovWedge(o, heading, hfov, rangeM, 24);
    if (Array.isArray(r) && r.length > 2) return r;
  } catch {
    /* shared stub */
  }
  return wedgeRing(o, heading, hfov, rangeM);
}

function fallbackHfov(cam: { sensorWidthMm: number | null; sensorHeightMm: number | null; focalLengthMm: number | null } | undefined): number | null {
  if (!cam) return null;
  try {
    const r = shared.cameraFov(cam);
    if (r) return r.hfovDeg;
  } catch {
    /* shared stub */
  }
  if (cam.sensorWidthMm && cam.focalLengthMm) return (2 * Math.atan(cam.sensorWidthMm / (2 * cam.focalLengthMm)) * 180) / Math.PI;
  return null;
}

/** "4.2 NM · 084° · +1,200 ft · closing 62 kt" */
function pairLabel(a: Kin, b: Kin): string {
  let distM: number;
  let brg: number;
  let altDiff: number | null = null;
  let closing: number | null = null;
  try {
    const g = relativeGeometry(
      { pos: a.pos, altM: a.altFt === null ? null : a.altFt * 0.3048, gsKt: a.gsKt, trackDeg: a.trackDeg, vsFpm: a.vsFpm },
      { pos: b.pos, altM: b.altFt === null ? null : b.altFt * 0.3048, gsKt: b.gsKt, trackDeg: b.trackDeg, vsFpm: b.vsFpm },
    );
    distM = g.distanceM;
    brg = g.bearingDeg;
    altDiff = g.altDiffFt;
    closing = g.closingKt;
  } catch {
    distM = distance(a.pos, b.pos);
    brg = bearing(a.pos, b.pos);
    altDiff = a.altFt !== null && b.altFt !== null ? b.altFt - a.altFt : null;
    // closing rate from 1 s of projected motion
    const step = (k: Kin) =>
      k.gsKt && k.trackDeg !== null ? destination(k.pos.lat, k.pos.lon, k.trackDeg, k.gsKt * KT_TO_MPS) : k.pos;
    const d1 = distance(step(a), step(b));
    closing = (distM - d1) / KT_TO_MPS;
  }
  const parts = [fmt.distance(distM), fmt.bearing(brg)];
  if (altDiff !== null) {
    const r = Math.round(altDiff / 10) * 10;
    parts.push(r === 0 ? '±0 ft' : `${r > 0 ? '+' : '−'}${fmt.int(Math.abs(r))} ft`);
  }
  if (closing !== null && Math.abs(closing) >= 2) parts.push(`${closing > 0 ? 'closing' : 'opening'} ${Math.round(Math.abs(closing))} kt`);
  return `<span class="num">${esc(parts.join(' · '))}</span>`;
}
